<script setup>
import { defineProps, onMounted, onUnmounted, ref } from 'vue';
import { fetchData } from '../../fetchData';
import VideoListItem from './VideoListItem.vue';
import VideoListItemText from './VideoListItemText.vue';
import VideoListSkeleton from './VideoListSkeleton.vue';

const props = defineProps({
    params: {
        type: Object,
        default: () => ({})
    }
});

const videos = ref([]);
const loading = ref(true);
const loadingMore = ref(false);
const error = ref(null);
const page = ref(1);
const limit = 15;
const hasMore = ref(true);
const scrollContainer = ref(null);

const fetchNews = async () => {
    if (loadingMore.value || !hasMore.value) {
        return;
    }

    if (page.value > 1) {
        loadingMore.value = true;
    }

    error.value = null;

    try {
        const urlParams = new URLSearchParams();

        for (const key in props.params) {
            const value = props.params[key];

            if (Array.isArray(value)) {
                value.forEach((v) => urlParams.append(`${key}[]`, v));
            } else {
                urlParams.set(key, value);
            }
        }

        urlParams.set('limit', limit);
        urlParams.set('page', page.value);

        const { result = {} } = await fetchData(`/api/v1/social-medias?${urlParams.toString()}`);

        if (result.data && result.data.length > 0) {
            videos.value.push(...result.data);
            page.value++;

            if (result.meta.current_page < result.meta.last_page) {
                hasMore.value = false;
            }
        } else {
            hasMore.value = false;
        }
    } catch (e) {
        error.value = 'Failed to load breaking news: ' + e.message;
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const handleScroll = () => {
    const el = scrollContainer.value;

    if (el) {
        const buffer = 100;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - buffer;

        if (isAtBottom) {
            fetchNews();
        }
    }
};

onMounted(() => {
    fetchNews();
    const el = scrollContainer.value;

    if (el) {
        el.addEventListener('scroll', handleScroll);
    }
});

onUnmounted(() => {
    const el = scrollContainer.value;

    if (el) {
        el.removeEventListener('scroll', handleScroll);
    }
});
</script>

<template>
    <div ref="scrollContainer" class="mt-2 overflow-y-auto h-full" @scroll="handleScroll">
        <div v-if="loading" class="space-y-2">
            <VideoListSkeleton v-for="n in 3" :key="`sk-load-${n}`" />
        </div>
        <div v-else-if="error" class="text-center text-red-500 py-4">
            {{ error }}
        </div>
        <div v-else-if="videos.length === 0" class="text-center text-gray-500 py-4">
            {{ $t('no_videos') }}
        </div>
        <div v-else class="space-y-2">
            <template v-for="(post, index) in videos" :key="post.id">
                <VideoListItem v-if="index === 0" :key="'video-' + post.id" :data="post" />
                <VideoListItemText v-else :key="'text-' + post.id" :data="post" />
            </template>
        </div>
        <div v-if="loadingMore" class="space-y-2 mt-2">
            <VideoListSkeleton key="sk-more" />
        </div>
    </div>
</template>
